import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import "./ComicReader.css";
import {
  cargarComicDesdeManifest,
  obtenerComicLocalParaLector,
} from "./comicReaderCatalog";
import { loadServerChapter } from "./mangaApi";

const numeroPaginaValido = (valor, totalPaginas) => {
  const numero = Number(valor);

  if (!Number.isInteger(numero) || numero < 0) return 0;
  if (totalPaginas > 0 && numero >= totalPaginas) return totalPaginas - 1;

  return numero;
};

export default function ComicReader() {
  const { mangaId, chapterId, comicId, page, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const esCapituloServidor = Boolean(mangaId && chapterId);

  const comicLocal = useMemo(
    () => (!esCapituloServidor && comicId ? obtenerComicLocalParaLector(comicId) : null),
    [comicId, esCapituloServidor]
  );

  const comicInicial = useMemo(() => {
    if (!esCapituloServidor && Array.isArray(location.state?.paginas) && location.state.paginas.length > 0) {
      return {
        comicId,
        titulo: location.state.titulo || comicLocal?.titulo || "Comic Viewer",
        paginas: location.state.paginas,
        enlace: location.state.from || comicLocal?.enlace || "/",
      };
    }

    return comicLocal;
  }, [comicId, comicLocal, esCapituloServidor, location.state]);

  const [comic, setComic] = useState(comicInicial);
  const [cargando, setCargando] = useState(Boolean(esCapituloServidor || (comicId && !comicInicial)));
  const [paginaActual, setPaginaActual] = useState(
    numeroPaginaValido(page ?? id, comicInicial?.paginas?.length || 0)
  );
  const [modoCascada, setModoCascada] = useState(false);

  const paginas = useMemo(() => comic?.paginas || [], [comic]);
  const titulo = comic?.titulo || "Comic Viewer";

  useEffect(() => {
    if (esCapituloServidor) {
      const controller = new AbortController();
      setCargando(true);
      loadServerChapter(mangaId, chapterId, controller.signal)
        .then((serverChapter) => setComic(serverChapter))
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.error("No se pudo cargar el capítulo del servidor:", error);
            setComic(null);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setCargando(false);
        });
      return () => controller.abort();
    }

    if (comicInicial) {
      setComic(comicInicial);
      setCargando(false);
      return;
    }

    if (!comicId) {
      setComic(null);
      setCargando(false);
      return;
    }

    const controller = new AbortController();
    setCargando(true);

    cargarComicDesdeManifest(comicId, controller.signal)
      .then((comicManifest) => setComic(comicManifest))
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("No se pudo cargar el manifiesto del cómic:", error);
          setComic(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setCargando(false);
      });

    return () => controller.abort();
  }, [chapterId, comicId, comicInicial, esCapituloServidor, mangaId]);

  useEffect(() => {
    setPaginaActual(numeroPaginaValido(page ?? id, paginas.length));
  }, [id, page, paginas.length]);

  const irAPagina = useCallback((numero) => {
    const pagina = numeroPaginaValido(numero, paginas.length);
    setPaginaActual(pagina);

    if (esCapituloServidor) {
      navigate(`/lector/${encodeURIComponent(mangaId)}/${encodeURIComponent(chapterId)}/${pagina}`, {
        replace: true,
      });
    } else if (comicId) {
      navigate(`/lector/${encodeURIComponent(comicId)}/${pagina}`, {
        replace: true,
        state: {
          paginas,
          titulo,
          from: comic?.enlace || location.state?.from || "/",
        },
      });
    }
  }, [chapterId, comic?.enlace, comicId, esCapituloServidor, location.state?.from, mangaId, navigate, paginas, titulo]);

  const siguiente = useCallback(() => {
    if (paginaActual < paginas.length - 1) irAPagina(paginaActual + 1);
  }, [irAPagina, paginaActual, paginas.length]);

  const anterior = useCallback(() => {
    if (paginaActual > 0) irAPagina(paginaActual - 1);
  }, [irAPagina, paginaActual]);

  const volver = () => navigate(location.state?.from || comic?.enlace || "/");

  const cambiarPagina = (e) => {
    irAPagina(Number(e.target.value));
  };

  const toggleCascada = () => {
    setModoCascada(!modoCascada);
  };

  // === ESCUCHAR TECLAS ===
  useEffect(() => {
    const manejarTeclas = (e) => {
      if (!modoCascada) {
        if (e.key === "ArrowRight") siguiente();
        else if (e.key === "ArrowLeft") anterior();
      }
    };

    window.addEventListener("keydown", manejarTeclas);
    return () => window.removeEventListener("keydown", manejarTeclas);
  }, [anterior, modoCascada, siguiente]);


  return (
    <div className="comic-container">
      {/* === BARRA SUPERIOR === */}
      <div className="comic-topbar">
        <button className="nav-btn" onClick={volver}>← Volver</button>
        <span className="comic-logo">⚡ {titulo}</span>
        <div className="comic-controls">
          <button className="nav-btn" onClick={toggleCascada}>
            🌊 {modoCascada ? "Normal" : "Cascada"}
          </button>

          {!modoCascada && (
            <>
              <button className="nav-btn" onClick={anterior}>◀ Anterior</button>
              <button className="nav-btn" onClick={siguiente}>Siguiente ▶</button>
              <select
                className="page-select"
                value={paginaActual}
                onChange={cambiarPagina}
              >
                {paginas.map((_, index) => (
                  <option key={index} value={index}>
                    Página {index + 1}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>
<div
  className={`comic-viewer ${!modoCascada ? "touch-nav" : ""}`}
  onTouchStart={(e) => {

    if (modoCascada) return;

    const toque = e.touches[0];

    const mitad = window.innerWidth / 2;

    if (toque.clientX < mitad) {
      anterior();
    } else {
      siguiente();
    }
  }}
>
    {cargando ? (
      <p style={{ color: "#00eaff" }}>Cargando páginas...</p>
    ) : paginas.length > 0 ? (
    modoCascada ? (
      // --- MODO CASCADA ---
      paginas.map((img, index) => (
        <img
          key={index}
          src={img}
          alt={`Página ${index + 1}`}
          className="comic-page cascada"
        />
      ))
    ) : (
      // --- MODO NORMAL ---
      <img
        src={paginas[paginaActual]}
        alt={`Página ${paginaActual + 1}`}
        className="comic-page"
      />
    )
  ) : (
    <div style={{ color: "#00eaff", textAlign: "center" }}>
      <p>⚠ No se encontraron páginas para este enlace.</p>
      {!comicId && (
        <p>El enlace antiguo no identifica el cómic. Ábrelo nuevamente desde su ficha.</p>
      )}
      <button className="nav-btn" onClick={() => navigate("/")}>
        Volver al inicio
      </button>
    </div>
  )}
</div>

      {/* === PIE DE PÁGINA === */}
      <div className="comic-footer">
        © 2025 TMOXNight — Lector Futurista 💫
      </div>
    </div>
  );
}
